// ...existing code...
// Modal de gestión de seriales guardados
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-unused-vars */
import moment from 'moment';
import ReactJson from 'react-json-view';
import React, {
  useState, useEffect, useRef,
} from 'react';
import { motion } from 'framer-motion';
// --- Overview Table Types ---
function DeviceOverviewTable({
  allDeviceRows,
  loading,
  loadingMore,
  hasMoreData,
  onLoadMore,
  search,
  setSearch,
  onSerialClick,
  savedSerialsList,
  serialSearchTerm,
  setSerialSearchTerm,
  handleApplySavedSerialConfig,
  handleDeleteSerialFromList,
  handleOpenManageSerialsModal,
  isManageSerialsModalOpen,
  currentSerialDescription,
  setCurrentSerialDescription,
  project,
  handleSaveCurrentSerialToList,
  handleCloseManageSerialsModal,
  statusFilter,
  handleStatusFilterChange,
  previousDeviceCount,
  setPreviousDeviceCount,
  isDefenderEnabled,
  onQuickSaveSerial,
  onRemoveSerial,
}) {
  // State for quick save modal
  const [quickSaveSerial, setQuickSaveSerial] = React.useState(null);
  const [quickSaveDescription, setQuickSaveDescription] = React.useState('');

  // Memoize the saved serials lookup for performance
  const savedSerialsSet = React.useMemo(() => {
    return new Set(savedSerialsList.map(s => s.serial));
  }, [savedSerialsList]);

  // Memoize the saved serials map for quick ID lookup
  const savedSerialsMap = React.useMemo(() => {
    return new Map(savedSerialsList.map(s => [s.serial, s.id]));
  }, [savedSerialsList]);

  // Handle quick save button click
  const handleQuickSaveClick = React.useCallback((serial, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (savedSerialsSet.has(serial)) {
      // If already saved, remove it
      const savedId = savedSerialsMap.get(serial);
      if (savedId && onRemoveSerial) {
        onRemoveSerial(savedId);
      }
    } else {
      // Open quick save modal
      setQuickSaveSerial(serial);
      setQuickSaveDescription('');
    }
  }, [savedSerialsSet, savedSerialsMap, onRemoveSerial]);

  // Handle quick save submit
  const handleQuickSaveSubmit = React.useCallback(() => {
    if (quickSaveSerial && quickSaveDescription.trim() && onQuickSaveSerial) {
      onQuickSaveSerial(quickSaveSerial, quickSaveDescription.trim());
      setQuickSaveSerial(null);
      setQuickSaveDescription('');
    }
  }, [quickSaveSerial, quickSaveDescription, onQuickSaveSerial]);

  // Close quick save modal
  const handleCloseQuickSave = React.useCallback(() => {
    setQuickSaveSerial(null);
    setQuickSaveDescription('');
  }, []);

  // Data is already filtered during loading, just sort it
  const sorted = allDeviceRows.sort((a, b) => (b.timestamp || 0) > (a.timestamp || 0) ? 1 : -1);

  // Track the number of devices for animation purposes - now managed by parent
  const [isAutoLoading, setIsAutoLoading] = React.useState(false);
  
  React.useEffect(() => {
    if (sorted.length > previousDeviceCount) {
      setPreviousDeviceCount(sorted.length);
      setIsAutoLoading(false); // Reset auto-loading flag when new data arrives
    }
  }, [sorted.length, previousDeviceCount, setPreviousDeviceCount]);

  // Check if we need to load more data to fill the screen
  const checkAndLoadMore = React.useCallback(() => {
    if (!hasMoreData || loading || loadingMore || isAutoLoading) {
      return;
    }

    // Get the table container to check if it needs more content
    const container = document.querySelector('[data-testid="devices-table-container"]');
    if (container) {
      const { scrollHeight, clientHeight } = container;
      // If content height is less than or equal to container height, we need more data
      if (scrollHeight <= clientHeight && sorted.length > 0) {
        setIsAutoLoading(true);
        onLoadMore();
      }
    }
  }, [hasMoreData, loading, loadingMore, isAutoLoading, sorted.length, onLoadMore]);

  // Auto-load more data if screen is not full
  React.useEffect(() => {

    // Only check after initial load is complete and we have some data
    if (!loading && sorted.length > 0 && !isAutoLoading && hasMoreData) {
      const timeoutId = setTimeout(checkAndLoadMore, 1000); // Increased delay to 1 second
      return () => clearTimeout(timeoutId);
    }
    
    // Additional check: if we have very few records and there should be more, force load
    if (!loading && !loadingMore && hasMoreData && sorted.length < 20 && sorted.length > 0) {

      const timeoutId = setTimeout(() => {
        if (!isAutoLoading) {
          setIsAutoLoading(true);
          onLoadMore();
        }
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [sorted.length, hasMoreData, loading, loadingMore, isAutoLoading, onLoadMore]);

  // Infinite scroll handler with debounce
  const handleScroll = React.useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    
    // Load more when user scrolls to 80% of the content and has more data (reduced threshold)
    if (scrollPercentage > 0.8 && hasMoreData && !loadingMore && !loading) {
      onLoadMore();
    }
  }, [hasMoreData, loadingMore, loading, onLoadMore]);

  // On search submit, go to detail for the entered serial
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (search.trim()) {
      onSerialClick(search.trim());
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
        <form onSubmit={handleSearchSubmit} className="flex flex-1 gap-2">
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Enter device serial"
            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="submit"
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
          >
            <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
            Go
          </button>
        </form>
      </div>

      {/* Period selector and device count moved above table */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex justify-between items-center mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200"
      >
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="status" className="text-sm text-gray-700 font-medium">Status:</label>
            <select
              id="status"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
            >
              <option value="connected">Connected</option>
              <option value="disconnected">Disconnected</option>
              {isDefenderEnabled && <option value="blocked">Blocked</option>}
            </select>
          </div>
        </div>
        
        {sorted.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2, delay: 0.1 }}
            className="text-sm text-gray-600 font-medium bg-white px-3 py-1 rounded-md border border-gray-300"
          >
            Showing {sorted.length} devices
          </motion.div>
        )}
      </motion.div>
      {isManageSerialsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Manage Saved Serials</h3>
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-700 mb-2">Save Current Serial</h4>
              <input
                type="text"
                value={search || ''}
                disabled
                className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 mb-3"
                placeholder="Current Device Serial"
              />
              <input
                type="text"
                value={currentSerialDescription}
                onChange={e => setCurrentSerialDescription(e.target.value)}
                placeholder="Description for this Serial (e.g., Test Machine A)"
                className="w-full p-2 border border-gray-300 rounded-md mb-3 focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="flex justify-end w-full mt-2">
                <button
                  onClick={handleSaveCurrentSerialToList}
                  disabled={!search.trim() || !currentSerialDescription.trim() || !project?.code}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium inline-flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-1">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75v-1.5A2.25 2.25 0 0 0 15 3h-6A2.25 2.25 0 0 0 6.75 5.25v13.19c0 1.04 1.21 1.64 2.02.98l3.23-2.7a.75.75 0 0 1 .98 0l3.23 2.7c.81.66 2.02.06 2.02-.98V8.25m-10.5 0h10.5" />
                  </svg>
                  Save Current Serial
                </button>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="text-md font-medium text-gray-700 mb-3">Saved Serials</h4>
              <input
                type="search"
                value={serialSearchTerm}
                onChange={e => setSerialSearchTerm(e.target.value)}
                placeholder="Search by Serial or Description"
                className="w-full p-2 border border-gray-300 rounded-md mb-4 focus:ring-blue-500 focus:border-blue-500"
              />
              {savedSerialsList.filter(
                (s) => s.serial.toLowerCase().includes(serialSearchTerm.toLowerCase()) ||
                  s.description.toLowerCase().includes(serialSearchTerm.toLowerCase())
              ).length === 0 ? (
                <p className="text-sm text-gray-500">
                  {savedSerialsList.length > 0 ? 'No serials match your search.' : 'No serials saved for this project yet.'}
                </p>
              ) : (
                <div className="max-h-60 overflow-y-auto scrollbar scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 border border-gray-200 rounded-md">
                  <ul className="divide-y divide-gray-200">
                    {savedSerialsList.filter(
                      (s) => s.serial.toLowerCase().includes(serialSearchTerm.toLowerCase()) ||
                        s.description.toLowerCase().includes(serialSearchTerm.toLowerCase())
                    ).map((s) => (
                      <li key={s.id} className="p-3 hover:bg-gray-100 flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{s.description}</p>
                          <p className="text-xs text-gray-500">
                            Serial: {s.serial} - Saved: {moment(s.date).format('MMM D, YYYY h:mm A')}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleApplySavedSerialConfig(s)}
                            title="Apply this serial"
                            className="p-1 text-blue-600 hover:text-blue-800"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75A2.25 2.25 0 0 0 14.25 4.5h-4.5A2.25 2.25 0 0 0 7.5 6.75v10.5A2.25 2.25 0 0 0 9.75 19.5h4.5A2.25 2.25 0 0 0 16.5 17.25V13.5" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15.75V8.25" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteSerialFromList(s.id)}
                            title="Delete this serial"
                            className="p-1 text-red-600 hover:text-red-800"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={handleCloseManageSerialsModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="border border-gray-200 rounded-lg shadow-sm overflow-hidden flex-1">
        <div 
          className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 h-full"
          style={{ height: 'calc(100vh - 320px)' }}
          onScroll={handleScroll}
          data-testid="devices-table-container"
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">Device Serial</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">Connection Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">Timestamp</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading && sorted.length === 0 ? (
                <LoadingTableRows />
              ) : sorted.length === 0 ? (
                <motion.tr
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <td colSpan={3} className="text-center py-16">
                    <div className="flex flex-col items-center">
                      <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-5.5a2 2 0 00-1.5.67l-1 1a2 2 0 01-1.5.67H9.5a2 2 0 01-1.5-.67l-1-1A2 2 0 005.5 13H0" />
                      </svg>
                      <p className="text-gray-500 font-medium text-lg">No devices found</p>
                      <p className="text-gray-400 text-sm mt-2">Try selecting another status</p>
                    </div>
                  </td>
                </motion.tr>
              ) : (
                sorted.map((device, index) => {
                  // Calculate staggered animation delay for new items
                  // For initial load, animate first batch with staggered effect
                  // For infinite scroll, animate only new items with staggered effect
                  const initialBatchSize = Math.min(sorted.length, previousDeviceCount || 12);
                  const isInitialLoad = previousDeviceCount === 0;
                  const isNewlyLoaded = index >= initialBatchSize && !isInitialLoad;
                  
                  let animationDelay = 0;
                  if (isInitialLoad) {
                    // Initial load: stagger all visible items
                    animationDelay = Math.min(index * 0.05, 0.5);
                  } else if (isNewlyLoaded) {
                    // New items: stagger only the newly loaded ones
                    const newItemIndex = index - initialBatchSize;
                    animationDelay = newItemIndex * 0.1;
                  }
                  
                  return (
                    <motion.tr
                      key={`${device.serial || 'unknown'}-${device.timestamp || 'no-time'}-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ 
                        delay: animationDelay, 
                        duration: 0.4,
                        ease: "easeOut"
                      }}
                      className="hover:bg-blue-50 transition-all duration-200 cursor-pointer border-b border-gray-100 last:border-b-0"
                      whileHover={{ scale: 1.01, backgroundColor: "rgb(239 246 255)" }}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => onSerialClick(device.serial)}
                          className="font-mono text-blue-600 underline hover:text-blue-800 focus:outline-none transition-colors duration-200 text-sm"
                        >
                          {device.serial}
                        </button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {device.status === 'connected' ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          ) : device.status === 'disconnected' ? (
                            <XCircleIcon className="h-5 w-5 text-red-500" />
                          ) : device.status === 'blocked' ? (
                            <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
                          ) : (
                            <InformationCircleIcon className="h-5 w-5 text-gray-400" />
                          )}
                          <span className={`capitalize text-sm font-medium ${device.status === 'blocked' ? 'text-orange-600' : ''}`}>{device.status}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {device.timestamp ? moment(device.timestamp).format('YYYY-MM-DD HH:mm:ss') : '-'}
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
          
          {/* Loading indicator for infinite scroll with enhanced animation */}
          {loadingMore && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="flex items-center justify-center py-8 bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-200"
            >
              <div className="flex items-center space-x-3">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full"
                />
                <motion.span 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="text-gray-700 font-medium"
                >
                  Loading more devices...
                </motion.span>
              </div>
            </motion.div>
          )}
          
          {/* End of data indicator with enhanced styling */}
          {!hasMoreData && sorted.length > 0 && !loading && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="text-center py-8 bg-gradient-to-r from-gray-50 to-green-50 border-t border-gray-200"
            >
              <div className="flex items-center justify-center">
                <motion.svg 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="w-5 h-5 mr-2 text-green-600" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                </motion.svg>
                <span className="text-gray-600 font-medium">All devices loaded</span>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Quick Save Modal */}
      {quickSaveSerial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4"
          >
            <div className="flex items-center gap-2 mb-4">
              <BookmarkIconSolid className="h-6 w-6 text-yellow-500" />
              <h3 className="text-lg font-semibold text-gray-900">Save Serial to Favorites</h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Serial: <span className="font-mono font-medium text-blue-600">{quickSaveSerial}</span>
            </p>
            <div className="mb-4">
              <label htmlFor="quick-save-description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                id="quick-save-description"
                type="text"
                value={quickSaveDescription}
                onChange={(e) => setQuickSaveDescription(e.target.value)}
                placeholder="Enter a description for this serial"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && quickSaveDescription.trim()) {
                    handleQuickSaveSubmit();
                  }
                  if (e.key === 'Escape') {
                    handleCloseQuickSave();
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCloseQuickSave}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleQuickSaveSubmit}
                disabled={!quickSaveDescription.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Save
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
import { useQuery } from '@tanstack/react-query';
import { useParams, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ShareIcon,
  ExclamationTriangleIcon,
  BookmarkIcon as BookmarkIconSolid,
} from '@heroicons/react/24/solid';
import { BookmarkIcon as BookmarkIconOutline } from '@heroicons/react/24/outline';
import {
  WifiIcon,
  CpuChipIcon,
  Squares2X2Icon,
  ServerStackIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

// Loading components
const LoadingSpinner = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3 }}
    className="flex flex-col items-center justify-center py-12"
  >
    <div className="relative">
      <div className="w-12 h-12 border-4 border-blue-100 rounded-full"></div>
      <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
    </div>
    <motion.p
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.3 }}
      className="text-gray-600 mt-4 font-medium"
    >
      Loading...
    </motion.p>
  </motion.div>
);

const LoadingTableRows = () => (
  <>
    {[...Array(12)].map((_, index) => (
      <motion.tr
        key={index}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ 
          delay: index * 0.08, 
          duration: 0.4,
          ease: "easeOut"
        }}
        className="animate-pulse border-b border-gray-100"
      >
        <td className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded-md w-36"></div>
        </td>
        <td className="px-4 py-4">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gray-200 rounded-full"></div>
            <div className="h-4 bg-gray-200 rounded-md w-24"></div>
          </div>
        </td>
        <td className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded-md w-40"></div>
        </td>
      </motion.tr>
    ))}
  </>
);

import PageBanner from '../../../components/utils/PageBanner';
import { stringHasValue } from '../../../utils/stringHasValue';
import GraphqlService from '../../../service/graphqlService';
import { useProject } from '../../../config/ProjectContext';
// QuickSightDashboard removed - now displayed on Home page (FR-020)
import { config } from '../../../config/awsExports';

const { env } = config;

// import FineGrainedPermissionsInfo from '../../Admin/FineGrainedPermissions/FineGrainedPermissionsInfo';
// import useIsDevicePermissionAllowed from '../../../hooks/useIsDevicePermissionAllowed';
import BrandEvents from './BrandEvents';
import BrandConsumptions from './BrandConsumptions';
import DeviceSettings from '../DeviceSettings';
import DeviceInfoTab from './DeviceInfoTab';
import DeploymentEventsHistory from './DeploymentEventsHistory';
import DeviceRawData from '../DeviceRawData';
import DefenderActionsTab from './DefenderActionsTab';

import cleanAllNullValues from '../../../utils/cleanAllNullValues';
import DateRangePicker from '../../../components/utils/DateRangePicker';

import {
  SHOW_TELEMETRY_EVENTS_PERMISSION,
  SHOW_BRAND_CONSUMPTIONS_PERMISSION,
  IS_ROOT,
} from '../../../utils/constants';
import Restricted from '../../../authorization/Restricted';
import ConnectivityStatusHistory from './ConnectivityStatusHistory';
import { useSnackbar } from '../../../providers/SnackbarContext';
import useAnalytics from '../../../hooks/useAnalytics';
import useBlockedDevices from '../../../hooks/useBlockedDevices';
import useRegionFeatureGate from '../../../hooks/useRegionFeatureGate';
import { isDefenderEnabledProject } from '../../../config/deviceDefender';

const HISTORY_CONTEXTS = {
  CONNECTIVITY: 'CONNECTIVITY',
  DEVICE: 'DEVICE',
  MODULE: 'MODULE',
  GATEWAY: 'GATEWAY',
  SETTINGS: 'SETTINGS',
  MODULE_SETTINGS: 'MODULE_SETTINGS',
};

// Visual config (icon + colors) for each history context, used by the custom dropdown
const HISTORY_CONTEXT_OPTIONS = [
  {
    value: HISTORY_CONTEXTS.CONNECTIVITY,
    label: 'Connectivity',
    Icon: WifiIcon,
    text: 'text-sky-700',
    bg: 'bg-sky-50',
    activeBg: 'bg-sky-100',
    ring: 'text-sky-500',
  },
  {
    value: HISTORY_CONTEXTS.DEVICE,
    label: 'Device',
    Icon: CpuChipIcon,
    text: 'text-indigo-700',
    bg: 'bg-indigo-50',
    activeBg: 'bg-indigo-100',
    ring: 'text-indigo-500',
  },
  {
    value: HISTORY_CONTEXTS.MODULE,
    label: 'Module',
    Icon: Squares2X2Icon,
    text: 'text-violet-700',
    bg: 'bg-violet-50',
    activeBg: 'bg-violet-100',
    ring: 'text-violet-500',
  },
  {
    value: HISTORY_CONTEXTS.GATEWAY,
    label: 'Gateway',
    Icon: ServerStackIcon,
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
    activeBg: 'bg-emerald-100',
    ring: 'text-emerald-500',
  },
  {
    value: HISTORY_CONTEXTS.SETTINGS,
    label: 'Device Settings',
    Icon: Cog6ToothIcon,
    text: 'text-amber-700',
    bg: 'bg-amber-50',
    activeBg: 'bg-amber-100',
    ring: 'text-amber-500',
  },
  {
    value: HISTORY_CONTEXTS.MODULE_SETTINGS,
    label: 'Module Settings',
    Icon: Cog6ToothIcon,
    text: 'text-rose-700',
    bg: 'bg-rose-50',
    activeBg: 'bg-rose-100',
    ring: 'text-rose-500',
  },
];

// Segmented control to pick the history context. All options are visible at once
// (icon + label per option) so users discover them without opening a dropdown.
const HistoryContextSegmented = ({ value, onChange, options }) => {
  const availableOptions = options && options.length ? options : HISTORY_CONTEXT_OPTIONS;

  return (
    <div
      role="group"
      aria-label="Telemetry history type"
      className="inline-flex flex-nowrap items-center gap-1 overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 p-1"
    >
      {availableOptions.map((option) => {
        const OptionIcon = option.Icon;
        const isSelected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onChange(option.value)}
            className={`flex h-[34px] shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-opacity-50 ${
              isSelected
                ? `${option.bg} ${option.text} shadow-sm`
                : 'bg-transparent text-gray-600 hover:bg-white hover:text-gray-900'
            }`}
          >
            <OptionIcon className="h-5 w-5" />
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};

HistoryContextSegmented.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.object),
};

HistoryContextSegmented.defaultProps = {
  options: HISTORY_CONTEXT_OPTIONS,
};

const HISTORY_PAGE_SIZE = 10;

const HISTORY_CONTEXT_HANDLERS = {
  [HISTORY_CONTEXTS.DEVICE]: {
    callApi: (base) => GraphqlService.getDeviceHistory(base),
    mapItem: (item, fallbackSerial) => ({
      deviceSerial: item?.serial || fallbackSerial,
      payloadSummary: summarizePayload(item?.extra),
      type: 'device',
      reason: `fw: ${item?.fw || 'N/A'} | hw: ${item?.hw || 'N/A'}`,
      updateAt: item?.received_on_utc || item?.processed_on_utc || null,
    }),
  },
  [HISTORY_CONTEXTS.GATEWAY]: {
    callApi: (base, { gatewaySerial }) => GraphqlService.getGatewayHistory({ ...base, gatewaySerial }),
    mapItem: (item, fallbackSerial) => ({
      deviceSerial: item?.device_serial || fallbackSerial,
      gatewayId: item?.serial || 'NA',
      payloadSummary: summarizePayload(item?.extra),
      type: 'gateway',
      reason: `app: ${item?.app || 'N/A'} | version: ${item?.version || 'N/A'}`,
      updateAt: item?.received_on_utc || item?.processed_on_utc || null,
    }),
  },
  [HISTORY_CONTEXTS.MODULE]: {
    callApi: (base, { moduleSerial }) => GraphqlService.getModuleHistory({ ...base, moduleSerial }),
    mapItem: (item, fallbackSerial) => ({
      deviceSerial: item?.device_serial || fallbackSerial,
      moduleId: item?.serial || 'NA',
      payloadSummary: summarizePayload(item?.extra),
      type: 'module',
      reason: `fw: ${item?.fw || 'N/A'} | hw: ${item?.hw || 'N/A'}`,
      updateAt: item?.received_on_utc || item?.processed_on_utc || null,
    }),
  },
  [HISTORY_CONTEXTS.SETTINGS]: {
    callApi: (base) => GraphqlService.getDeviceSnapHistory(base),
    mapItem: (item, fallbackSerial) => ({
      deviceSerial: item?.serial || fallbackSerial,
      payloadSummary: summarizePayload(item?.settings),
      type: 'settings',
      reason: 'N/A',
      updateAt: item?.received_on_utc || item?.processed_on_utc || null,
    }),
  },
  [HISTORY_CONTEXTS.MODULE_SETTINGS]: {
    callApi: (base, { moduleSerial }) => GraphqlService.getModuleSnapHistory({ ...base, moduleSerial }),
    mapItem: (item, fallbackSerial) => ({
      deviceSerial: item?.device_serial || fallbackSerial,
      moduleId: item?.serial || 'NA',
      payloadSummary: summarizePayload(item?.settings),
      type: 'module_settings',
      reason: 'N/A',
      updateAt: item?.received_on_utc || item?.processed_on_utc || null,
    }),
  },
};

const summarizePayload = (value) => {
  if (value === undefined || value === null) return 'NA';

  if (typeof value === 'string') {
    return value.length > 180 ? `${value.slice(0, 177)}...` : value;
  }

  if (typeof value !== 'object') {
    return String(value);
  }

  const keys = Object.keys(value);
  if (keys.length === 0) {
    return 'NA';
  }

  const priorityKeys = ['reason', 'message', 'status', 'code', 'value', 'description', 'detail'];
  const selectedKeys = [
    ...priorityKeys.filter((key) => keys.includes(key)),
    ...keys.filter((key) => !priorityKeys.includes(key)),
  ].slice(0, 4);

  const compact = selectedKeys.reduce((acc, key) => {
    acc[key] = value[key];
    return acc;
  }, {});

  try {
    const serialized = JSON.stringify(compact);
    return serialized.length > 180 ? `${serialized.slice(0, 177)}...` : serialized;
  } catch (error) {
    return 'NA';
  }
};

// Helper functions for localStorage (remains unchanged)
const loadSerialsFromStorage = (projectId) => {
  if (!projectId) return [];
  const key = `savedSerialsV2:${projectId}`;
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error("Error loading serials from localStorage:", error);
    return [];
  }
};

const saveSerialToStorage = (projectId, serialToSave, description) => {
  if (!projectId || !serialToSave || !description) return loadSerialsFromStorage(projectId);
  const key = `savedSerialsV2:${projectId}`;
  const existingSerials = loadSerialsFromStorage(projectId);
  const newSerialEntry = {
    id: Date.now().toString(),
    serial: serialToSave,
    description,
    date: new Date().toISOString(),
  };
  const updatedSerials = [...existingSerials, newSerialEntry];
  try {
    localStorage.setItem(key, JSON.stringify(updatedSerials));
  } catch (error) {
    console.error("Error saving serial to localStorage:", error);
  }
  return updatedSerials;
};

const deleteSerialInStorage = (projectId, serialIdToDelete) => {
  if (!projectId || !serialIdToDelete) return loadSerialsFromStorage(projectId);
  const key = `savedSerialsV2:${projectId}`;
  const existingSerials = loadSerialsFromStorage(projectId);
  const updatedSerials = existingSerials.filter(s => s.id !== serialIdToDelete);
  try {
    localStorage.setItem(key, JSON.stringify(updatedSerials));
  } catch (error) {
    console.error("Error deleting serial from localStorage:", error);
  }
  return updatedSerials;
};

export default function CertificateStatus() {
  const [statusFilter, setStatusFilter] = useState('connected'); // 'connected', 'disconnected', 'blocked'
  
  // Analytics hook - initialized early for use in handlers
  const { setAnalytics } = useAnalytics();
  
  // Handler for status filter change
  const handleStatusFilterChange = (e) => {
    const newStatusFilter = e.target.value;
    
    // Update state
    setStatusFilter(newStatusFilter);
    
    // Complete reset of all states when filter changes
    setAllDeviceRows([]);
    setOverviewNextToken('');
    setHasMoreData(true);
    setPreviousDeviceCount(0);
    setPreviousToken('');
    setLoadingMoreOverview(false);
    setLoadingOverview(false);
  };
  // Estado para seriales guardados y campos relacionados
  const [savedSerialsList, setSavedSerialsList] = useState([]);
  const [serialSearchTerm, setSerialSearchTerm] = useState('');
  const [isManageSerialsModalOpen, setIsManageSerialsModalOpen] = useState(false);
  const [currentSerialDescription, setCurrentSerialDescription] = useState('');
  
  // Quick save state for device detail view - mirrors DeviceOverviewTable logic
  const [detailQuickSaveSerial, setDetailQuickSaveSerial] = useState(null);
  const [detailQuickSaveDescription, setDetailQuickSaveDescription] = useState('');
  
  const { project, setProject } = useProject();

  // Memoize saved serials lookup for the detail view save button
  const savedSerialsSet = React.useMemo(() => {
    return new Set(savedSerialsList.map(s => s.serial));
  }, [savedSerialsList]);

  // Memoize saved serials map for quick ID lookup
  const savedSerialsMap = React.useMemo(() => {
    return new Map(savedSerialsList.map(s => [s.serial, s.id]));
  }, [savedSerialsList]);

  // Device Defender: Check if blocked devices filter should be visible.
  // Also hidden when the China data region is selected (not available there).
  const isFeatureAvailable = useRegionFeatureGate();
  const isDefenderEnabled = isDefenderEnabledProject(project?.code) && isFeatureAvailable('deviceDefender');

  // Device Defender: Fetch blocked devices when 'blocked' status filter is active
  const {
    data: blockedDevicesData,
    isLoading: isLoadingBlockedDevices,
    error: blockedDevicesError,
    refetch: refetchBlockedDevices,
    fetchStatus: blockedDevicesFetchStatus,
  } = useBlockedDevices(project?.code, {
    page: 0,
    pageSize: 100,
    enabled: statusFilter === 'blocked' && isDefenderEnabled, // Only fetch when blocked filter is selected and Defender is available
  });
  
  // --- Overview Table State for Infinite Scroll ---
  const [showOverviewTable, setShowOverviewTable] = useState(true);
  const [allDeviceRows, setAllDeviceRows] = useState([]);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingMoreOverview, setLoadingMoreOverview] = useState(false);
  const [overviewSearch, setOverviewSearch] = useState('');
  const [overviewNextToken, setOverviewNextToken] = useState('');
  const [hasMoreData, setHasMoreData] = useState(true);
  const [previousDeviceCount, setPreviousDeviceCount] = useState(0);
  const [previousToken, setPreviousToken] = useState(''); // Track previous token to detect repeats
  
  // Request ID ref to prevent stale responses from overwriting newer data
  const currentRequestRef = useRef(0);

  // --- Serial click handler ---
  const handleOverviewSerialClick = (serial) => {
    // Track navigation from blocked devices list
    if (statusFilter === 'blocked') {
      setAnalytics({
        event: 'button_clicked',
        extraParams: {
          name: 'device_management-overview-blocked_devices-device_clicked',
          device_serial: serial,
        },
      });
    }
    setIsLoadingMachineStatus(true); // Start loading immediately
    setDeviceSerial(serial);
    setShowOverviewTable(false);
    setTabValue('1');
    // Navigate to the URL with the project and device serial
    history.push(`/statistics/deviceOverview/${project?.code}/${serial}`);
  };

  // --- Effect to fetch BLOCKED devices (uses TanStack Query data) ---
  useEffect(() => {
    if (!project?.code || !showOverviewTable || statusFilter !== 'blocked') return;
    
    // Show loading while fetching
    if (isLoadingBlockedDevices) {
      setLoadingOverview(true);
      return;
    }
    
    // Handle error
    if (blockedDevicesError) {
      setAllDeviceRows([]);
      setHasMoreData(false);
      setLoadingOverview(false);
      return;
    }
    
    // Process blocked devices data
    if (blockedDevicesData?.blockedDevices) {
      const blockedRows = blockedDevicesData.blockedDevices.map(device => ({
        serial: device.deviceSerial,
        status: 'blocked',
        timestamp: device.insertedOn,
      }));
      setAllDeviceRows(blockedRows);
      setHasMoreData(false);
      setPreviousDeviceCount(blockedRows.length);
    } else {
      setAllDeviceRows([]);
      setHasMoreData(false);
    }
    setLoadingOverview(false);
  }, [project?.code, showOverviewTable, statusFilter, blockedDevicesData, blockedDevicesError, isLoadingBlockedDevices]);

  // --- Effect to fetch CONNECTED/DISCONNECTED devices (uses GraphQL API) ---
  useEffect(() => {
    if (!project?.code || !showOverviewTable) return;
    if (statusFilter === 'blocked') return; // Skip for blocked filter
    
    // Increment request ID to track this specific request
    const requestId = ++currentRequestRef.current;
    const capturedFilter = statusFilter;
    
    setLoadingOverview(true);
    setAllDeviceRows([]);
    setHasMoreData(true);
    
    const end = moment();
    const start = moment().subtract(1, 'month');

    GraphqlService.getDeviceConnectivityStatusHistory({
      projectId: project.code,
      serialNumber: undefined,
      from: start.toISOString(),
      to: end.toISOString(),
      paginationToken: '',
    })
      .then(history => {
        // Ignore stale responses
        if (requestId !== currentRequestRef.current) return;
        
        const events = history?.events || [];
        let unknownDeviceCounter = 1;

        const allDevices = events
          .filter(event => {
            if (!event) return false;
            const eventStatus = event.type || 'unknown';
            return eventStatus === capturedFilter;
          })
          .map(event => ({
            serial: event.deviceSerial || `unknown-device-${unknownDeviceCounter++}`,
            status: event.type || 'unknown',
            timestamp: event.updateAt || null,
          }))
          .sort((a, b) => moment(b.timestamp || 0).valueOf() - moment(a.timestamp || 0).valueOf());

        setAllDeviceRows(allDevices);
        setOverviewNextToken(history?.nextToken || '');
        setHasMoreData(!!history?.nextToken);
      })
      .catch((error) => {
        if (requestId !== currentRequestRef.current) return;
        console.error('Error loading devices:', error);
        setAllDeviceRows([]);
        setOverviewNextToken('');
        setHasMoreData(false);
      })
      .finally(() => {
        if (requestId === currentRequestRef.current) {
          setLoadingOverview(false);
        }
      });
  }, [project?.code, showOverviewTable, statusFilter]);

  // --- Handler for search in overview ---
  const handleOverviewSearch = (e) => {
    e.preventDefault();
    // Reset data and reload when searching
    if (overviewSearch.trim()) {
      setAllDeviceRows([]);
      setOverviewNextToken('');
      setHasMoreData(true);
      // The useEffect will trigger a new data fetch when the search term changes
    }
  };

  // --- Handler for loading more devices ---
  const handleLoadMoreDevices = React.useCallback(async () => {
    if (!project?.code || !hasMoreData || loadingMoreOverview || !overviewNextToken) {
      return;
    }

    setLoadingMoreOverview(true);

    try {
      // Always use month period
      const end = moment();
      const start = moment().subtract(1, 'month');

      const history = await GraphqlService.getDeviceConnectivityStatusHistory({
        projectId: project.code,
        serialNumber: undefined,
        from: start.toISOString(),
        to: end.toISOString(),
        paginationToken: overviewNextToken,
      });

      const events = history?.events || [];
      
      // Filter and process new events
      let unknownDeviceCounter = allDeviceRows.length + 1;
      const newDevices = events
        .filter(event => {
          if (!event) return false;
          const eventStatus = event.type || 'unknown';
          return statusFilter === 'all' || eventStatus === statusFilter;
        })
        .map(event => ({
          serial: event.deviceSerial || `unknown-device-${unknownDeviceCounter++}`,
          status: event.type || 'unknown',
          timestamp: event.updateAt || null,
        }))
        .sort((a, b) => moment(b.timestamp || 0).valueOf() - moment(a.timestamp || 0).valueOf());

      // Append new devices to existing ones
      setAllDeviceRows(prev => [...prev, ...newDevices]);
      setOverviewNextToken(history?.nextToken || '');
      setHasMoreData(!!history?.nextToken);
      
      // Check for duplicate tokens to prevent infinite loops
      if (history?.nextToken === previousToken) {
        console.warn('Duplicate pagination token detected, stopping pagination');
        setHasMoreData(false);
      } else {
        setPreviousToken(history?.nextToken || '');
      }
    } catch (error) {
      console.error('Error loading more devices:', error);
      setHasMoreData(false);
    } finally {
      setLoadingMoreOverview(false);
    }
  }, [project?.code, hasMoreData, loadingMoreOverview, overviewNextToken, statusFilter, allDeviceRows.length, previousToken]);

  // --- Handler for returning to overview table ---
  const handleBackToOverview = () => {
    // If user came from Home page, go back in history instead of pushing new route
    if (history.location?.state?.from === 'home') {
      history.goBack();
      return;
    }
    
    setShowOverviewTable(true);
    setDeviceSerial('');
    setTabValue('1');
    // Navigate back to the base overview URL (remove deviceSerial from URL)
    history.push('/statistics/deviceOverview');
  };
  const { deviceSerial: deviceSerialFromParams, projectCode: projectCodeFromParams } = useParams();
  const history = useHistory();
  const [deviceSerial, setDeviceSerial] = useState('');
  const [machineInfo, setMachineInfo] = useState(null);
  const [rawMachineInfo, setRawMachineInfo] = useState(null); // Store unprocessed data for JSON export
  const [isLoadingMachineStatus, setIsLoadingMachineStatus] = useState(false);
  const [isLoadingConnectivityStatusHistory, setIsLoadingConnectivityStatusHistory] = useState(false);
  const [tabValue, setTabValue] = useState('1');
  // const [isValidDevice, setIsValidDevice] = useState(false);
  const [connectivityStatus, setConnectivityStatus] = useState(null);
  const [connectivityStatusHistoryToken, setConnectivityStatusHistoryToken] = useState('');
  // Eliminado: const [isLoadingConnectivityStatus, setIsLoadingConnectivityStatus] = useState(false);
  // const { isAllowed } = useIsDevicePermissionAllowed(deviceSerial);
  const [shadowState, setShadowState] = useState(null);
  const [isLoadingShadowState, setIsLoadingShadowState] = useState(false);
  const [useProcessedTimestamp, setUseProcessedTimestamp] = useState(false);
  // Eliminado: const [noResultsDialogOpen, setNoResultsDialogOpen] = useState(false);
  const [triggerEventsSearch, setTriggerEventsSearch] = useState(false);
  const [triggerConsumptionsSearch, setTriggerConsumptionsSearch] = useState(false);

  // (mantener estos estados para la gestión de seriales guardados)

  // Ref para el DeviceInfoTab
  const deviceInfoTabRef = useRef();
  // Tracks the device serial for which the presence history graph has already been loaded,
  // so it only loads the first time and not on every connectivity history search.
  const presenceGraphLoadedForSerialRef = useRef(null);
  const openSnackBar = useSnackbar();

  // Analytics handlers for main buttons (info-bookmark, info-share, info-search, info)

  const handleTabAnalytics = (tabValue) => {
    let name = '';
    switch (tabValue) {
      case '1':
        name = 'device_management-overview-details';
        break;
      case '2':
        name = 'device_management-overview-connectivity_history';
        break;
      case '3':
        name = 'device_management-overview-shadow_wrapper';
        break;
      case '4':
        name = 'device_management-overview-consumptions';
        break;
      case '5':
        name = 'device_management-overview-events';
        break;
      case '6':
        name = 'device_management-overview-settings';
        break;
      case '7':
        name = 'device_management-overview-deployment_history';
        break;
      case '8':
        name = 'device_management-overview-raw_data';
        break;
      case '9':
        name = 'device_management-overview-defender_actions';
        break;
      default:
        name = 'device_management-overview-details';
    }
    setAnalytics({
      event: 'button_clicked',
      extraParams: {
        name,
      }
    });
  };

  useEffect(() => {
    setAnalytics({
      event: 'section_loaded',
      sectionId: 'device_management-overview',
    });
  }, [setAnalytics]);

  // Handler for sharing device URL
  const handleShareDevice = async () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'device_management-overview-info-share' }
    });
    if (!deviceSerial) {
      openSnackBar('No device serial available to share.', 'error');
      return;
    }

    try {
      // Generate the correct URL with React Router path including project
      const shareUrl = `${window.location.origin}/#/statistics/deviceOverview/${encodeURIComponent(project?.code)}/${encodeURIComponent(deviceSerial)}`;

      // Copy to clipboard
      await navigator.clipboard.writeText(shareUrl);

      // Show success message
      openSnackBar('Device URL copied to clipboard!', 'success');
    } catch (error) {
      console.error('Failed to copy URL to clipboard:', error);
      openSnackBar('Failed to copy URL to clipboard.', 'error');
    }
  };
  useEffect(() => {
    handleClear();
  }, [project]);

  useEffect(() => {
    // If we have a projectCode from URL params and it's different from current project
    const loadProjectFromUrl = async () => {
      if (projectCodeFromParams && project?.code !== projectCodeFromParams) {
        try {
          // Fetch all projects and find the one matching the URL
          const projects = await GraphqlService.getProjects();
          const matchingProject = projects?.find(p => p.code === projectCodeFromParams);
          
          if (matchingProject) {
            // Set the project from URL
            setProject(matchingProject);
            localStorage.setItem('project', JSON.stringify(matchingProject));
          } else {
            console.warn('Project code from URL not found:', projectCodeFromParams);
          }
        } catch (error) {
          console.error('Error loading project from URL:', error);
        }
      }
    };
    
    loadProjectFromUrl();
    
    if (deviceSerialFromParams) {
      setDeviceSerial(deviceSerialFromParams);
      setShowOverviewTable(false); // Hide overview table and show device detail
      setTabValue('1'); // Set to first tab (Details)
    } else {
      setDeviceSerial(''); // Clear deviceSerial if no parameter is present
      setShowOverviewTable(true); // Show overview table when no deviceSerial in URL
    }
  }, [deviceSerialFromParams, projectCodeFromParams, project?.code, setProject]);


  useEffect(() => {
    if (project?.code) {
      setSavedSerialsList(loadSerialsFromStorage(project.code));
    } else {
      setSavedSerialsList([]);
    }
  }, [project?.code]);

  const { refetch: fetchDeviceInfo } = useQuery(
    ['fetchDeviceInfo', project?.code, deviceSerial],
    async () => {
      const deviceInfoResponse = await GraphqlService.getDeviceInfo(
        { projectId: project.code, serialNumber: deviceSerial }
      );
      if (deviceInfoResponse?.hasModulesInfo) {
        // parse the moduleInfo field
        try {
          deviceInfoResponse.modules = JSON.parse(deviceInfoResponse.modules);
        } catch (error) {
          console.error('Error parsing moduleInfo JSON:', error);
          deviceInfoResponse.modules = {};
        }
      }

      if (deviceInfoResponse?.extrasHasInfo) {
        // parse the extrasInfo field
        try {
          deviceInfoResponse.extra = JSON.parse(deviceInfoResponse.extra);
        } catch (error) {
          console.error('Error parsing extraInfo JSON:', error);
          deviceInfoResponse.extra = {};
        }
      }

      if (deviceInfoResponse?.hasDeviceGatewayInfo) {
        // parse the machineInfo field
        try {
          deviceInfoResponse.device_gateway = JSON.parse(deviceInfoResponse.device_gateway);
        } catch (error) {
          console.error('Error parsing machineInfo JSON:', error);
          deviceInfoResponse.device_gateway = {};
        }
      }

      // delete all the booleans that are not needed
      delete deviceInfoResponse.hasModulesInfo;
      delete deviceInfoResponse.extrasHasInfo;
      delete deviceInfoResponse.hasDeviceGatewayInfo;

      // Store the raw data before cleaning nulls
      const rawDataForExport = { ...deviceInfoResponse };

      // delete all null values of the object deviceInfoResponse for display
      const cleanedDeviceInfoResponse = cleanAllNullValues(deviceInfoResponse);

      return { cleaned: cleanedDeviceInfoResponse, raw: rawDataForExport };
    },
    {
      enabled:
        // isValidDevice && 
        stringHasValue(deviceSerial),
      onSuccess: (data) => {
        setMachineInfo(data.cleaned);
        setRawMachineInfo(data.raw);
        setIsLoadingMachineStatus(false);
      },
    },
  );

  const [connectivityStatusHistory, setConnectivityStatusHistory] = useState([]);
  
  // State for presence history graph (uses the selected date range)
  const [presenceHistory30Days, setPresenceHistory30Days] = useState([]);
  const [isLoadingPresenceHistory, setIsLoadingPresenceHistory] = useState(false);

  // Set initial date range based on period
  const [selectedStartDate, setSelectedStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return moment(date);
  });
  const [selectedEndDate, setSelectedEndDate] = useState(moment(new Date()));

  const [selectedStartTime, setSelectedStartTime] = useState('00:00');
  const [selectedEndTime, setSelectedEndTime] = useState('23:59');
  const [historyContext, setHistoryContext] = useState(HISTORY_CONTEXTS.CONNECTIVITY);
  const [historyModuleId, setHistoryModuleId] = useState('');
  const [historyGatewayId, setHistoryGatewayId] = useState('');

  // Auto-populate gateway serial and first module from device info when context changes
  React.useEffect(() => {
    if (historyContext === HISTORY_CONTEXTS.GATEWAY) {
      const gatewaySerial = rawMachineInfo?.device_gateway?.serial || '';
      setHistoryGatewayId(gatewaySerial);
    } else if (historyContext === HISTORY_CONTEXTS.MODULE
      || historyContext === HISTORY_CONTEXTS.MODULE_SETTINGS) {
      const modules = Array.isArray(rawMachineInfo?.modules) ? rawMachineInfo.modules : [];
      if (modules.length > 0 && !historyModuleId) {
        setHistoryModuleId(modules[0].serial);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyContext, rawMachineInfo]);

  // Hide Module/Module Settings options when the device has no modules, and Gateway when
  // it has no gateway, so the dropdown only shows applicable contexts.
  const availableHistoryOptions = React.useMemo(() => {
    const hasModules = Array.isArray(rawMachineInfo?.modules) && rawMachineInfo.modules.length > 0;
    const hasGateway = Boolean(rawMachineInfo?.device_gateway?.serial);
    return HISTORY_CONTEXT_OPTIONS.filter((option) => {
      if ((option.value === HISTORY_CONTEXTS.MODULE
        || option.value === HISTORY_CONTEXTS.MODULE_SETTINGS) && !hasModules) return false;
      if (option.value === HISTORY_CONTEXTS.GATEWAY && !hasGateway) return false;
      return true;
    });
  }, [rawMachineInfo]);

  const mapConnectivityEventsToRows = React.useCallback((events = []) => (
    events.map((event) => ({
      deviceSerial: event?.deviceSerial || deviceSerial || 'NA',
      moduleId: 'NA',
      gatewayId: 'NA',
      eventType: event?.type || 'unknown',
      payloadSummary: event?.reason || 'NA',
      type: event?.type || 'unknown',
      reason: event?.reason || 'NA',
      updateAt: event?.updateAt || null,
      principalID: event?.principalID || null,
    }))
  ), [deviceSerial]);

  const callHistoryApi = React.useCallback(async (pageNumber = 0, fromOverride, toOverride, limitOverride) => {
    const handler = HISTORY_CONTEXT_HANDLERS[historyContext];
    if (!handler) throw new Error(`No handler for history context: ${historyContext}`);
    const base = {
      projectId: Number(project?.code),
      serialNumber: deviceSerial,
      from: fromOverride || selectedStartDate.toISOString(),
      to: toOverride || selectedEndDate.toISOString(),
      limit: limitOverride || HISTORY_PAGE_SIZE,
      pageNumber,
    };
    const extras = { gatewaySerial: historyGatewayId, moduleSerial: historyModuleId };
    const data = await handler.callApi(base, extras);
    return {
      rows: (data?.items || []).map((item) => handler.mapItem(item, deviceSerial)),
      isLast: data?.isLast,
      pageNumber: data?.pageNumber,
    };
  }, [project?.code, deviceSerial, selectedStartDate, selectedEndDate, historyContext, historyGatewayId, historyModuleId]);

  const hasValidHistoryContext = React.useCallback(() => {
    if ((historyContext === HISTORY_CONTEXTS.MODULE
      || historyContext === HISTORY_CONTEXTS.MODULE_SETTINGS) && !historyModuleId) {
      openSnackBar('Please select a module from the module selector.', 'error');
      return false;
    }

    if (historyContext === HISTORY_CONTEXTS.GATEWAY && !historyGatewayId) {
      openSnackBar('No gateway information available for this device.', 'error');
      return false;
    }

    return true;
  }, [historyContext, historyModuleId, historyGatewayId, openSnackBar]);

  const fetchDeviceConnectivityStatusHistory = async (customPaginationToken = null) => {
    try {
      if (!project?.code || !deviceSerial) {
        return;
      }

      if (!hasValidHistoryContext()) {
        return;
      }

      setIsLoadingConnectivityStatusHistory(true);
      const tokenToUse = customPaginationToken !== null ? customPaginationToken : connectivityStatusHistoryToken;
      const pageNumber = Number(tokenToUse) || 0;

      let mappedItems = [];
      let nextPageToken = '';

      if (historyContext === HISTORY_CONTEXTS.CONNECTIVITY) {
        const data = await GraphqlService.getDeviceConnectivityStatusHistory({
          projectId: Number(project?.code),
          serialNumber: deviceSerial,
          from: selectedStartDate.toISOString(),
          to: selectedEndDate.toISOString(),
          paginationToken: tokenToUse || '',
        });
        mappedItems = mapConnectivityEventsToRows(data?.events || []);
        nextPageToken = data?.nextToken || '';
      } else {
        const result = await callHistoryApi(pageNumber);
        mappedItems = result.rows;
        nextPageToken = result.isLast ? '' : String((result.pageNumber ?? pageNumber) + 1);
      }

      setConnectivityStatusHistory(mappedItems);
      setConnectivityStatusHistoryToken(nextPageToken);

      if (pageNumber === 0 && mappedItems.length === 0) {
        openSnackBar(
          historyContext === HISTORY_CONTEXTS.CONNECTIVITY
            ? 'No connectivity history found for the selected filters.'
            : 'No history found for the selected filters.',
          'info',
        );
      }
    } catch (error) {
      console.error('Error fetching connectivity status history:', error);
      openSnackBar(error?.message || 'Failed to load history.', 'error');
    } finally {
      setIsLoadingConnectivityStatusHistory(false);
    }
  };

  // Fetch presence history for the graph using the selected date range
  // This fetches all available pages for accurate graph display.
  const fetchPresenceHistoryForGraph = async () => {
    try {
      if (!project?.code || !deviceSerial) {
        return;
      }

      if (!hasValidHistoryContext()) {
        return;
      }

      setIsLoadingPresenceHistory(true);

      const today = moment().endOf('day');
      const effectiveEndDate = selectedEndDate.isAfter(today) ? today : selectedEndDate;
      const fromISO = moment(selectedStartDate).startOf('day').toISOString();
      const toISO = moment(effectiveEndDate).endOf('day').toISOString();

      let allEvents = [];
      const MAX_GRAPH_PAGES = 25;

      for (let page = 0; page < MAX_GRAPH_PAGES; page += 1) {
        let pageData;
        if (historyContext === HISTORY_CONTEXTS.CONNECTIVITY) {
          const data = await GraphqlService.getDeviceConnectivityStatusHistory({
            projectId: Number(project?.code),
            serialNumber: deviceSerial,
            from: fromISO,
            to: toISO,
            paginationToken: page === 0 ? '' : String(page),
          });
          allEvents = allEvents.concat(mapConnectivityEventsToRows(data?.events || []));
          if (!data?.nextToken) break;
        } else {
          // eslint-disable-next-line no-await-in-loop
          pageData = await callHistoryApi(page, fromISO, toISO, 100);
          allEvents = allEvents.concat(pageData.rows);
          if (pageData?.isLast) break;
        }
      }

      setPresenceHistory30Days(allEvents);
    } catch (error) {
      console.error('Error fetching presence history for graph:', error);
    } finally {
      setIsLoadingPresenceHistory(false);
    }
  };

  const { refetch: fetchDeviceConnectivityStatus } = useQuery(
    ['deviceConnectivityStatus', project?.code, deviceSerial],
    async () => GraphqlService.getDeviceConnectivityStatus(
      {
        projectId: project.code,
        serialNumber: deviceSerial,
      },
    ),
    {
      enabled:
        // isValidDevice && 
        stringHasValue(deviceSerial),
      onSuccess: (data) => {
        setConnectivityStatus(data);
      },
    },
  );

  const { refetch: fetchShadowState } = useQuery(
    ['getShadowState', project?.code, deviceSerial],
    async () => GraphqlService.getShadowState(
      { projectId: project.code, serialNumber: deviceSerial },
    ),
    {
      enabled: false,
      onSuccess: (data) => {
        if (data?.reported?.machineInfo) {
          try {
            setShadowState({
              ...data,
              reported: {
                ...data.reported,
                machineInfo: data?.reported?.machineInfo ? JSON.parse(data.reported.machineInfo) : {},
              },
            });
          } catch (error) {
            console.error('Error parsing machineInfo JSON:', error);
            setShadowState(data);
          }
        } else {
          setShadowState(data);
        }
        setIsLoadingShadowState(false);
      },
    },
  );

  const handleSearchConnectivityStatusHistoryAnalyticsClick = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: {
        name: 'device_management-overview-connectivity_history-search',
      }

    });
  };

  const handleSearchConnectivityStatusHistory = (options = {}) => {
    const { forceGraphReload = false } = options;
    if (!deviceSerial) {
      openSnackBar('Please enter a device serial first.', 'error');
      return false;
    }
    // if (!isValidDevice) {
    //   openSnackBar('Device does not have the required permissions.', 'error');
    //   return null;
    // }
    handleSearchConnectivityStatusHistoryAnalyticsClick();
    
    // Reset pagination state for fresh search
    setConnectivityStatusHistoryToken('');
    setConnectivityStatusHistory([]);
    setPage(0);
    setPaginationTokens(['']);
    
    // Start fresh search with empty token
    fetchDeviceConnectivityStatusHistory('');
    
    // Load the presence history graph the first time for this device, or whenever the
    // caller forces a reload (e.g. the date range changed). Changing the context combo
    // box does NOT reload the graph.
    if (forceGraphReload || presenceGraphLoadedForSerialRef.current !== deviceSerial) {
      presenceGraphLoadedForSerialRef.current = deviceSerial;
      fetchPresenceHistoryForGraph();
    }
    
    return true;
  };

  // Auto-trigger the history search when the context combo box (or the module/gateway
  // selector) changes, so the user does not need to click "Search" manually.
  const isFirstHistoryContextRender = useRef(true);
  useEffect(() => {
    if (isFirstHistoryContextRender.current) {
      isFirstHistoryContextRender.current = false;
      return;
    }
    // Only auto-search while viewing the connectivity history tab for a selected device
    if (tabValue !== '2' || !deviceSerial || !project?.code) return;
    // For MODULE/GATEWAY wait until the required identifier has been auto-populated
    if ((historyContext === HISTORY_CONTEXTS.MODULE
      || historyContext === HISTORY_CONTEXTS.MODULE_SETTINGS) && !historyModuleId) return;
    if (historyContext === HISTORY_CONTEXTS.GATEWAY && !historyGatewayId) return;
    handleSearchConnectivityStatusHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyContext, historyModuleId, historyGatewayId]);

  const handleSearchDeviceSerial = React.useCallback(() => {
    if (!stringHasValue(deviceSerial)) {
      return null;
    }
    if (!project?.code) {
      return null;
    }
    // if (!isValidDevice) {
    //   return null;
    // }
    // Trigger clearing of device raw data when searching for a new device
    setShouldClearRawData(true);
    fetchDeviceInfo();
    fetchDeviceConnectivityStatus();
    setIsLoadingMachineStatus(true);

    // Trigger notable events search if on the Device Info tab
    if (tabValue === '1' && deviceInfoTabRef.current) {
      deviceInfoTabRef.current.fetchNotableEvents();
    }

    return true;
  }, [deviceSerial, project?.code, fetchDeviceInfo, fetchDeviceConnectivityStatus, tabValue]);

  useEffect(() => {
    if (
      deviceSerialFromParams &&
      deviceSerial === deviceSerialFromParams &&
      project?.code)
    // isValidDevice
    {
      handleSearchDeviceSerial();
    }
  }, [
    deviceSerial,
    project?.code,
    // isValidDevice, 
    deviceSerialFromParams,
    handleSearchDeviceSerial]);

  const [shouldClearRawData, setShouldClearRawData] = useState(false);

  const handleClear = () => {
    setDeviceSerial('');
    setMachineInfo(null);
    setRawMachineInfo(null);
    setIsLoadingMachineStatus(false);
    setIsLoadingConnectivityStatusHistory(false);
    setTabValue('1');
    setUseProcessedTimestamp(false);
    setConnectivityStatus(null);
    setConnectivityStatusHistory([]);
    setHistoryContext(HISTORY_CONTEXTS.CONNECTIVITY);
    setPage(0);
    setPaginationTokens(['']);
    setConnectivityStatusHistoryToken('');
    setPresenceHistory30Days([]);
    setShadowState(null);
    // Allow the presence graph to load again for the next device
    presenceGraphLoadedForSerialRef.current = null;
    // Trigger clearing of device raw data
    setShouldClearRawData(true);
  };

  const handleTabSwitch = (newTabValue) => {
    if (!isDeviceSerialValid() && newTabValue !== '1') {
      openSnackBar('Please enter a device serial first.', 'error');
      return;
    }
    setTabValue(newTabValue);

    if (newTabValue === '1' && deviceSerial && deviceInfoTabRef.current) {
      deviceInfoTabRef.current.fetchNotableEvents();
    }
    if (newTabValue === '2' && deviceSerial) {
      handleSearchConnectivityStatusHistory();
    }
    if (newTabValue === '3' && deviceSerial) {
      fetchShadowState();
      setIsLoadingShadowState(true);
    }
    if (newTabValue === '4' && deviceSerial) {
      setTriggerConsumptionsSearch(v => !v);
    }
    if (newTabValue === '5' && deviceSerial) {
      setTriggerEventsSearch(v => !v);
    }
  };


  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [paginationTokens, setPaginationTokens] = useState(['']);
  const handleChangePage = (event, newPage) => {
    if (newPage === page) {
      return;
    }

    // Scroll to top of the page when pagination changes
    window.scrollTo({
      top: 40,
      behavior: 'smooth'
    });

    if (newPage > page) {
      // Next page requires a backend token from the current page response.
      if (!connectivityStatusHistoryToken) {
        return;
      }

      setPaginationTokens((previousTokens) => {
        const updatedTokens = [...previousTokens];
        updatedTokens[newPage] = connectivityStatusHistoryToken;
        return updatedTokens;
      });
      setPage(newPage);
      fetchDeviceConnectivityStatusHistory(connectivityStatusHistoryToken);
    } else if (newPage < page) {
      const prevToken = paginationTokens[newPage] || '';
      setPage(newPage);
      fetchDeviceConnectivityStatusHistory(prevToken);
    }
  };

  const isDeviceSerialValid = () => stringHasValue(deviceSerial);

  // Removed automatic refetch on token change - now handled explicitly in pagination


  const handleDateChange = (startDate, endDate) => {
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
  };

  const handleOpenManageSerialsModal = () => {
    if (project?.code) {
      setSavedSerialsList(loadSerialsFromStorage(project.code));
    }
    setIsManageSerialsModalOpen(true);
  };

  const handleCloseManageSerialsModal = () => {
    setIsManageSerialsModalOpen(false);
    setCurrentSerialDescription('');
    setSerialSearchTerm('');
  };

  // Save the serial from the search input (overviewSearch) if in the modal, else fallback to deviceSerial
  const handleSaveCurrentSerialToList = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'device_management-overview-info-bookmark' }
    });
    // Use the value from the search input (overviewSearch) for the modal
    const serialToSave = overviewSearch && overviewSearch.trim() ? overviewSearch.trim() : deviceSerial;
    if (!serialToSave || !currentSerialDescription.trim() || !project?.code) {
      openSnackBar('Device serial and description cannot be empty.', 'error');
      return;
    }
    const updatedList = saveSerialToStorage(project.code, serialToSave, currentSerialDescription.trim());
    setSavedSerialsList(updatedList);
    setCurrentSerialDescription('');
  };

  const handleApplySavedSerialConfig = (serialConfig) => {
    const selectedSerial = serialConfig.serial;
    setDeviceSerial(selectedSerial);
    setIsLoadingMachineStatus(true);
    setShowOverviewTable(false);
    setTabValue('1');
    handleCloseManageSerialsModal();
    
    // Navigate to the URL with the project and device serial
    history.push(`/statistics/deviceOverview/${project?.code}/${selectedSerial}`);
  };

  const handleDeleteSerialFromList = (serialId) => {
    if (!project?.code) return;
    const updatedList = deleteSerialInStorage(project.code, serialId);
    setSavedSerialsList(updatedList);
  };

  // Quick save serial from the table row (used by bookmark icon)
  const handleQuickSaveSerial = (serial, description) => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'device_management-overview-quick-save-serial' }
    });
    if (!serial || !description || !project?.code) {
      openSnackBar('Device serial and description cannot be empty.', 'error');
      return;
    }
    const updatedList = saveSerialToStorage(project.code, serial, description);
    setSavedSerialsList(updatedList);
    openSnackBar(`Serial ${serial} saved to favorites!`, 'success');
  };

  // Handle quick save button click in device detail header - toggle save/unsave
  const handleDetailQuickSaveClick = React.useCallback((serial, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    
    if (savedSerialsSet.has(serial)) {
      // If already saved, remove it
      const savedId = savedSerialsMap.get(serial);
      if (savedId) {
        handleDeleteSerialFromList(savedId);
      }
    } else {
      // Open quick save modal
      setDetailQuickSaveSerial(serial);
      setDetailQuickSaveDescription('');
    }
  }, [savedSerialsSet, savedSerialsMap, handleDeleteSerialFromList]);

  // Handle detail quick save submit
  const handleDetailQuickSaveSubmit = React.useCallback(() => {
    if (detailQuickSaveSerial && detailQuickSaveDescription.trim()) {
      handleQuickSaveSerial(detailQuickSaveSerial, detailQuickSaveDescription.trim());
      setDetailQuickSaveSerial(null);
      setDetailQuickSaveDescription('');
    }
  }, [detailQuickSaveSerial, detailQuickSaveDescription, handleQuickSaveSerial]);

  // Close detail quick save modal
  const handleCloseDetailQuickSave = React.useCallback(() => {
    setDetailQuickSaveSerial(null);
    setDetailQuickSaveDescription('');
  }, []);

  const deviceShadowIsVisible = project?.code !== 5;

  // Check if it's a Purina project - based on project names that contain "Purina" or specific codes
  // This is a placeholder - the actual logic should be confirmed with business requirements
  const isPurinaProject = project?.name?.toLowerCase().includes('purina') ||
    project?.code === 1001 || // Based on message type definitions for Purina PoC
    project?.code === 1002;   // Based on message type definitions for Purina US "Astro" PoC

  // Settings tab should only be visible for Momento project (code 5) and project 34
  // Exclude Vertuo (code 28) and Vertuo G2B (code 10) projects
  const isSettingsVisible = project?.code === 5 || project?.code === '5' || project?.code === 34 || project?.code === '34';


  const tabItems = [
    { label: "Details", value: "1", disabled: false },
    { label: "Telemetry History", value: "2", disabled: !isDeviceSerialValid() },
    { label: "Shadow Wrapper", value: "3", disabled: !isDeviceSerialValid(), hidden: !deviceShadowIsVisible },
    { label: "Consumptions", value: "4", disabled: !isDeviceSerialValid(), hidden: isPurinaProject },
    { label: "Events", value: "5", disabled: !isDeviceSerialValid() },
    { label: "Settings", value: "6", disabled: !isDeviceSerialValid(), hidden: !isSettingsVisible },
    { label: "Deployment History", value: "7", disabled: !isDeviceSerialValid() },
    { label: "Raw Data", value: "8", disabled: !isDeviceSerialValid() },
    { label: "Defender Actions", value: "9", disabled: !isDeviceSerialValid(), hidden: !isDefenderEnabled },
  ];


  return (
    <div id="device-overview" className="bg-white min-h-screen">
      <PageBanner title="OVERVIEW" />
      {showOverviewTable ? (
        <div className="flex flex-col min-h-screen">
          {/* QuickSight Dashboard removed - now displayed on Home page (FR-020) */}
          
          <div className="flex-grow p-6 bg-white">
          
          <DeviceOverviewTable
            allDeviceRows={allDeviceRows}
            loading={loadingOverview || (statusFilter === 'blocked' && isLoadingBlockedDevices)}
            loadingMore={loadingMoreOverview}
            hasMoreData={statusFilter === 'blocked' ? false : hasMoreData}
            onLoadMore={handleLoadMoreDevices}
            search={overviewSearch}
            setSearch={setOverviewSearch}
            onSerialClick={handleOverviewSerialClick}
            savedSerialsList={savedSerialsList}
            serialSearchTerm={serialSearchTerm}
            setSerialSearchTerm={setSerialSearchTerm}
            handleApplySavedSerialConfig={handleApplySavedSerialConfig}
            handleDeleteSerialFromList={handleDeleteSerialFromList}
            handleOpenManageSerialsModal={handleOpenManageSerialsModal}
            isManageSerialsModalOpen={isManageSerialsModalOpen}
            currentSerialDescription={currentSerialDescription}
            setCurrentSerialDescription={setCurrentSerialDescription}
            project={project}
            handleSaveCurrentSerialToList={handleSaveCurrentSerialToList}
            handleCloseManageSerialsModal={handleCloseManageSerialsModal}
            statusFilter={statusFilter}
            handleStatusFilterChange={handleStatusFilterChange}
            previousDeviceCount={previousDeviceCount}
            setPreviousDeviceCount={setPreviousDeviceCount}
            isDefenderEnabled={isDefenderEnabled}
            onQuickSaveSerial={handleQuickSaveSerial}
            onRemoveSerial={handleDeleteSerialFromList}
          />
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 px-6 pt-6 pb-2">
            <button
              onClick={handleBackToOverview}
              className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              title="Back to Overview"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5 mr-1 text-blue-500">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
              Back to Overview
            </button>
            {deviceSerial && (
              <div className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded text-blue-700 font-mono text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5 text-blue-400">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                <span>Serial:</span>
                <span className="font-semibold">{deviceSerial}</span>
              </div>
            )}
            {deviceSerial && (
              <button
                onClick={(e) => handleDetailQuickSaveClick(deviceSerial, e)}
                className={`inline-flex items-center px-3 py-1 rounded focus:outline-none focus:ring-2 transition-colors duration-200 ${
                  savedSerialsSet.has(deviceSerial)
                    ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 focus:ring-yellow-400'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-400'
                }`}
                title={savedSerialsSet.has(deviceSerial) ? 'Remove from saved serials' : 'Save serial to favorites'}
              >
                {savedSerialsSet.has(deviceSerial) ? (
                  <BookmarkIconSolid className="h-4 w-4 mr-1" />
                ) : (
                  <BookmarkIconOutline className="h-4 w-4 mr-1" />
                )}
                {savedSerialsSet.has(deviceSerial) ? 'Saved' : 'Save'}
              </button>
            )}
            {deviceSerial && (
              <button
                onClick={handleShareDevice}
                className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-400 transition-colors duration-200"
                title="Share device URL"
              >
                <ShareIcon className="h-4 w-4 mr-1" />
                Share
              </button>
            )}
          </div>
          {/* Tabs */}
          <div className="px-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-4 overflow-x-auto" aria-label="Tabs">
                {tabItems.filter(tab => !tab.hidden).map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => { handleTabAnalytics(tab.value); handleTabSwitch(tab.value); }}
                    disabled={tab.disabled}
                    className={`whitespace-nowrap py-3 px-3 border-b-2 font-medium text-sm focus:outline-none
                      ${tabValue === tab.value
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                      ${tab.disabled ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
          <div className="p-1">
            <motion.div
              key={tabValue}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {tabValue === '1' && (
                <div className="bg-white">
                  {isLoadingMachineStatus ? (
                    <LoadingSpinner />
                  ) : machineInfo ? (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                    >
                      <DeviceInfoTab
                        ref={deviceInfoTabRef}
                        machineInfo={machineInfo}
                        rawMachineInfo={rawMachineInfo}
                        isLoading={isLoadingMachineStatus}
                        connectivityStatus={connectivityStatus}
                        deviceSerial={deviceSerial}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className="flex flex-col items-center justify-center py-12"
                    >
                      <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="text-gray-500 text-lg font-medium">No device info loaded</span>
                      <span className="text-gray-400 text-sm mt-1">Search for a device to view its information</span>
                    </motion.div>
                  )}
                </div>
              )}
              {tabValue === '2' && (
                <div>
                  <div className="flex flex-col sm:flex-row justify-between items-end mb-4 gap-4">
                    <DateRangePicker
                      onDateChange={handleDateChange}
                      setSelectedStartDate={setSelectedStartDate}
                      selectedStartDate={selectedStartDate}
                      setSelectedStartTime={setSelectedStartTime}
                      selectedStartTime={selectedStartTime}
                      setSelectedEndDate={setSelectedEndDate}
                      selectedEndDate={selectedEndDate}
                      setSelectedEndTime={setSelectedEndTime}
                      selectedEndTime={selectedEndTime}
                      onApply={() => handleSearchConnectivityStatusHistory({ forceGraphReload: true })}
                      maxDaysBetweenDates={31}
                      disableFutureDates
                    />
                  </div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <ConnectivityStatusHistory
                      data={connectivityStatusHistory || []}
                      isLoading={isLoadingConnectivityStatusHistory}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      handleChangePage={handleChangePage}
                      count={connectivityStatusHistory?.length || 0}
                      connectivityStatusHistoryToken={connectivityStatusHistoryToken}
                      selectedStartDate={selectedStartDate}
                      selectedEndDate={selectedEndDate}
                      presenceHistoryData={presenceHistory30Days}
                      isLoadingPresenceHistory={isLoadingPresenceHistory}
                      historyContext={historyContext}
                      tableControls={(
                        <div className="flex flex-row flex-wrap gap-2 items-center my-4">
                          <HistoryContextSegmented
                            value={historyContext}
                            options={availableHistoryOptions}
                            onChange={(newValue) => {
                              setHistoryContext(newValue);
                              setConnectivityStatusHistory([]);
                              setConnectivityStatusHistoryToken('');
                              setPaginationTokens(['']);
                              setPage(0);
                            }}
                          />
                          {(historyContext === HISTORY_CONTEXTS.MODULE
                            || historyContext === HISTORY_CONTEXTS.MODULE_SETTINGS) && (
                            <select
                              value={historyModuleId}
                              onChange={(e) => setHistoryModuleId(e.target.value)}
                              className="h-[42px] px-3 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
                            >
                              {(Array.isArray(rawMachineInfo?.modules) ? rawMachineInfo.modules : []).map((m) => (
                                <option key={m.serial} value={m.serial}>
                                  {m.model ? `Module ${m.model} — ${m.serial}` : m.serial}
                                </option>
                              ))}
                            </select>
                          )}
                          {historyContext === HISTORY_CONTEXTS.GATEWAY && (
                            <span className="inline-flex items-center h-[42px] px-3 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700">
                              {historyGatewayId || 'No gateway'}
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </motion.div>
                </div>
              )}
              {tabValue === '3' && (
                <div>
                  {isLoadingShadowState ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className="flex items-center justify-center py-12"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-gray-600 font-medium text-lg">Loading shadow state...</span>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                      className="bg-gray-50 p-4 rounded-md shadow"
                    >
                      <ReactJson
                        src={cleanAllNullValues(shadowState || {})}
                        theme="rjv-default"
                        collapsed={false}
                        displayDataTypes={false}
                        enableClipboard
                        style={{ fontSize: '0.875rem', lineHeight: '1.25' }}
                      />
                    </motion.div>
                  )}
                </div>
              )}
              {tabValue === '4' && (
                <div>
                  <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <span className="text-sm text-gray-700">Use processed timestamp:</span>
                      <button
                        type="button"
                        className={`${useProcessedTimestamp ? 'bg-blue-600' : 'bg-gray-200'} 
                                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer 
                                  rounded-full border-2 border-transparent transition-colors 
                                  duration-200 ease-in-out focus:outline-none focus:ring-2 
                                  focus:ring-blue-500 focus:ring-offset-2`}
                        role="switch"
                        aria-checked={useProcessedTimestamp}
                        onClick={() => setUseProcessedTimestamp(!useProcessedTimestamp)}
                      >
                        <span
                          aria-hidden="true"
                          className={`${useProcessedTimestamp ? 'translate-x-5' : 'translate-x-0'} 
                                    inline-block h-5 w-5 transform rounded-full bg-white shadow 
                                    ring-0 transition duration-200 ease-in-out`}
                        />
                      </button>
                    </label>
                  </div>
                  <Restricted to={SHOW_BRAND_CONSUMPTIONS_PERMISSION}>
                    <BrandConsumptions
                      deviceSerial={deviceSerial}
                      useProcessedTimestamp={useProcessedTimestamp}
                      triggerConsumptionsSearch={triggerConsumptionsSearch}
                    />
                  </Restricted>
                </div>
              )}
              {tabValue === '5' && (
                <div>
                  <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <span className="text-sm text-gray-700">Use processed timestamp:</span>
                      <button
                        type="button"
                        className={`${useProcessedTimestamp ? 'bg-blue-600' : 'bg-gray-200'} 
                                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer 
                                  rounded-full border-2 border-transparent transition-colors 
                                  duration-200 ease-in-out focus:outline-none focus:ring-2 
                                  focus:ring-blue-500 focus:ring-offset-2`}
                        role="switch"
                        aria-checked={useProcessedTimestamp}
                        onClick={() => setUseProcessedTimestamp(!useProcessedTimestamp)}
                      >
                        <span
                          aria-hidden="true"
                          className={`${useProcessedTimestamp ? 'translate-x-5' : 'translate-x-0'} 
                                    inline-block h-5 w-5 transform rounded-full bg-white shadow 
                                    ring-0 transition duration-200 ease-in-out`}
                        />
                      </button>
                    </label>
                  </div>
                  <Restricted to={SHOW_TELEMETRY_EVENTS_PERMISSION}>
                    <BrandEvents
                      deviceSerial={deviceSerial}
                      useProcessedTimestamp={useProcessedTimestamp}
                      triggerEventsSearch={triggerEventsSearch}
                    />
                  </Restricted>
                </div>
              )}
              {tabValue === '6' && (
                <div>
                  <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <span className="text-sm text-gray-700">Use processed timestamp:</span>
                      <button
                        type="button"
                        className={`${useProcessedTimestamp ? 'bg-blue-600' : 'bg-gray-200'} 
                                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer 
                                  rounded-full border-2 border-transparent transition-colors 
                                  duration-200 ease-in-out focus:outline-none focus:ring-2 
                                  focus:ring-blue-500 focus:ring-offset-2`}
                        role="switch"
                        aria-checked={useProcessedTimestamp}
                        onClick={() => setUseProcessedTimestamp(!useProcessedTimestamp)}
                      >
                        <span
                          aria-hidden="true"
                          className={`${useProcessedTimestamp ? 'translate-x-5' : 'translate-x-0'} 
                                    inline-block h-5 w-5 transform rounded-full bg-white shadow 
                                    ring-0 transition duration-200 ease-in-out`}
                        />
                      </button>
                    </label>
                  </div>
                  <Restricted to={SHOW_TELEMETRY_EVENTS_PERMISSION}>
                    <DeviceSettings
                      deviceSerial={deviceSerial}
                      useProcessedTimestamp={useProcessedTimestamp}
                    />
                  </Restricted>
                </div>
              )}
              {tabValue === '7' && (
                <DeploymentEventsHistory deviceSerial={deviceSerial} />
              )}
              {tabValue === '8' && (
                <DeviceRawData
                  deviceSerial={deviceSerial}
                  shouldClear={shouldClearRawData}
                  onClearComplete={() => setShouldClearRawData(false)}
                />
              )}
              {tabValue === '9' && (
                <DefenderActionsTab deviceSerial={deviceSerial} />
              )}
            </motion.div>
          </div>
        </>
      )}
      
      {/* Quick Save Modal for Device Detail View */}
      {detailQuickSaveSerial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4"
          >
            <div className="flex items-center gap-2 mb-4">
              <BookmarkIconSolid className="h-6 w-6 text-yellow-500" />
              <h3 className="text-lg font-semibold text-gray-900">Save Serial to Favorites</h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Serial: <span className="font-mono font-medium text-blue-600">{detailQuickSaveSerial}</span>
            </p>
            <div className="mb-4">
              <label htmlFor="detail-quick-save-description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                id="detail-quick-save-description"
                type="text"
                value={detailQuickSaveDescription}
                onChange={(e) => setDetailQuickSaveDescription(e.target.value)}
                placeholder="Enter a description for this serial"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && detailQuickSaveDescription.trim()) {
                    handleDetailQuickSaveSubmit();
                  }
                  if (e.key === 'Escape') {
                    handleCloseDetailQuickSave();
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCloseDetailQuickSave}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDetailQuickSaveSubmit}
                disabled={!detailQuickSaveDescription.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Save
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
